import { useMutation, useQueryClient } from '@tanstack/react-query';
import { manageContactsFromLabels } from '../../gpanel-contacts-api.ts';
import { useMemo, useState } from 'react';
import { Messages } from '../../../../../utils/message-constants.ts';
import { useStore } from '../../../../state/zustand-store.ts';
import { GPanelContactsAddToLabelDialogProps as GPanelContactsManageLabelDialogProps } from '../../shared/types.ts';
import { useLabelsQuery } from '../../gpanel-contacts-queries.ts';
import GpanelContactsAddLabelDialog from '../gpanel-contacts-labels/dialogs/gpanel-contacts-add-label-dialog.tsx';
import { useAnalytics } from '../../../../../shared/analytics/create-events.ts';
import ChangeLogDialog from '../../../../../shared/change-log-dialog.tsx';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list.ts';
import {
  GPanelContactsPermissions,
  hasPermission,
} from '../../../../../utils/roles/client-roles.ts';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';
import {
  updateContactLabelCachesOnAdd,
  updateContactLabelCachesOnRemove,
} from '../../shared/utils.ts';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Check from '@mui/icons-material/Check';
import Label from '@mui/icons-material/Label';

export default function GPanelContactsManageLabelDialog({
  handleClose,
  contactIds,
  anchorEl,
  contacts,
}: GPanelContactsManageLabelDialogProps) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const { changeLogRoles } = useUserSettings();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const [openAddLabelDialog, setOpenAddLabelDialog] = useState(false);

  const commonLabel = useMemo(() => {
    const contactSelectedLabelIds: number[][] = (contacts ?? [])
      .filter((row) => contactIds.includes(Number(row.id)))
      .map((el) => el.labelIds ?? []);

    if (!contactSelectedLabelIds || contactSelectedLabelIds.length === 0) {
      return [];
    }
    if (contactSelectedLabelIds.length === 1) {
      return Array.from(new Set(contactSelectedLabelIds[0]));
    }

    let commonElements = new Set(contactSelectedLabelIds[0]);

    for (let i = 1; i < contactSelectedLabelIds.length; i++) {
      const currentArray = contactSelectedLabelIds[i];
      const currentArraySet = new Set(currentArray);
      const nextCommonElements = new Set<number>();
      for (const element of commonElements) {
        if (currentArraySet.has(element)) {
          nextCommonElements.add(element);
        }
      }
      commonElements = nextCommonElements;

      if (commonElements.size === 0) {
        break;
      }
    }

    return Array.from(commonElements);
  }, [contactIds, contacts]);

  const [selectedLabelIds, setSelectedLabelIds] =
    useState<number[]>(commonLabel);
  const [hasChanged, setHasChanged] = useState(false);

  const labelsToAdd = selectedLabelIds.filter(
    (id) => !commonLabel.includes(id)
  );
  const labelsToRemove = commonLabel.filter(
    (id) => !selectedLabelIds.includes(id)
  );

  const manageContactsFromLabelMutation = useMutation({
    mutationFn: () =>
      manageContactsFromLabels(
        labelsToAdd,
        labelsToRemove,
        contactIds,
        changeLogMessage.trim() || undefined
      ),
    onSuccess: () => {
      if (labelsToAdd.length > 0) {
        updateContactLabelCachesOnAdd(
          queryClient,
          contactIds,
          labelsToAdd,
          contacts
        );
      }
      if (labelsToRemove.length > 0) {
        labelsToRemove.forEach((labelId) => {
          updateContactLabelCachesOnRemove(queryClient, contactIds, labelId);
        });
      }

      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated("Contact's label(s) were"),
      });
      handleClose();
      setChangeLogMessage('');
    },
    onError: () => {
      setSnackbar({
        open: true,
        severity: 'error',
        message: Messages.Errors.Network.Updating("contact's label(s)"),
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.gpanel_contacts.edit_contact_labels, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.gpanelContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { data: labels = [], isFetching: isLabelsFetching } = useLabelsQuery();

  // Filter out trashed labels to prevent users from assigning contacts to trashed labels
  const activeLabels = labels.filter((label) => !label.trashed);

  const isPending = manageContactsFromLabelMutation.isPending;

  const toggleLabelSelection = (labelId: number) => {
    if (selectedLabelIds.includes(labelId)) {
      setSelectedLabelIds(selectedLabelIds.filter((id) => id !== labelId));
    } else {
      setSelectedLabelIds((prev) => [...prev, labelId]);
    }
    setHasChanged(true);
  };

  const handleApply = () => {
    if (isPending) return;

    if (
      !openChangeLogDialog &&
      hasPermission(
        changeLogRoles,
        GPanelContactsPermissions.ModifyGpanelContactLabelMembers.value
      )
    ) {
      setOpenChangeLogDialog(true);
      return;
    }

    if (labelsToAdd.length === 0 && labelsToRemove.length === 0) {
      handleClose();
      return;
    }

    manageContactsFromLabelMutation.mutate();
  };

  return (
    <>
      <Menu
        open
        anchorEl={anchorEl}
        onClose={() => {
          if (isPending) return;
          handleClose();
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
            sx: {
              py: 0,
            },
          },
          paper: {
            style: {
              maxHeight: 400,
              overflow: 'hidden',
              maxWidth: 280,
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
        sx={{
          '& .MuiMenu-list': {
            padding: 0,
          },
        }}
      >
        <Box sx={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: 320 }}>
          {isLabelsFetching && (
            <MenuItem disabled sx={{ opacity: 0.6 }}>
              Loading…
            </MenuItem>
          )}

          {activeLabels.map((lbl) => {
            const isSelected = selectedLabelIds.includes(lbl.id);

            return (
              <MenuItem
                key={lbl.id}
                onClick={() => toggleLabelSelection(lbl.id)}
                disabled={isPending}
              >
                <Label sx={{ mr: 1, opacity: 0.54 }} />

                {lbl.name.length > 20 ? (
                  <Tooltip title={lbl.name}>
                    <Typography component="span">{`${lbl.name.slice(
                      0,
                      20
                    )}...`}</Typography>
                  </Tooltip>
                ) : (
                  lbl.name
                )}

                {isSelected ? (
                  <Check sx={{ ml: 1, opacity: 0.54 }} />
                ) : (
                  <Check sx={{ ml: 1, visibility: 'hidden' }} />
                )}
              </MenuItem>
            );
          })}
        </Box>

        <MenuItem divider sx={{ pointerEvents: 'none', opacity: 0.6, p: 0 }} />

        {hasChanged ? (
          <MenuItem onClick={handleApply}>
            {isPending ? (
              <CircularProgress sx={{ mx: 'auto' }} size={25} />
            ) : (
              <>
                <Check fontSize="small" sx={{ mr: 1 }} />
                Apply
              </>
            )}
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              setOpenAddLabelDialog(true);
            }}
          >
            <Add fontSize="small" sx={{ mr: 1 }} />
            Create New Label
          </MenuItem>
        )}
      </Menu>

      {openAddLabelDialog && (
        <GpanelContactsAddLabelDialog
          handleClose={() => setOpenAddLabelDialog(false)}
        />
      )}

      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={handleApply}
        mutateLoading={isPending}
      />
    </>
  );
}
